/**
 * Tracks dynamically added script tags to help identify sources of errors.
 * Creates Sentry breadcrumbs when scripts are injected into the DOM.
 * Scans inline scripts for suspicious promise rejection patterns.
 * Useful for debugging issues from GTM inline tags or other dynamic scripts.
 */
import * as Sentry from '@sentry/nuxt'

export default defineNuxtPlugin(() => {
  if (import.meta.client) {
    // Patterns that indicate potentially problematic promise handling in inline scripts
    const suspiciousPatterns = [
      /\.reject\(\s*\)/, // .reject() with no argument
      /reject\(\s*undefined\s*\)/, // reject(undefined)
      /reject\(\s*null\s*\)/, // reject(null)
      /reject\(\s*\)/, // reject() with no argument in promise executor
    ]

    // Create a MutationObserver to watch for script tags being added
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        // Check added nodes
        for (const node of mutation.addedNodes) {
          if (node.nodeName === 'SCRIPT') {
            const script = node as HTMLScriptElement
            const scriptContent = script.textContent || ''
            const isInline = !script.src

            // Only scan inline scripts for suspicious patterns
            let hasSuspiciousPattern = false
            let matchedPattern = ''
            if (isInline && scriptContent) {
              for (const pattern of suspiciousPatterns) {
                if (pattern.test(scriptContent)) {
                  hasSuspiciousPattern = true
                  matchedPattern = pattern.source
                  break
                }
              }
            }

            // Extract useful information about the script
            const scriptInfo = {
              src: script.src || 'inline',
              type: script.type || 'text/javascript',
              async: script.async,
              defer: script.defer,
              // For suspicious scripts: show FULL content to debug
              // For normal scripts: show preview (first 100 chars)
              inlineContent:
                isInline && scriptContent
                  ? hasSuspiciousPattern
                    ? scriptContent // Full content for suspicious scripts
                    : scriptContent.substring(0, 100) +
                      (scriptContent.length > 100 ? '...' : '')
                  : null,
              // Where in DOM it was added
              parentElement: script.parentElement?.tagName || 'unknown',
              // Flag suspicious patterns
              suspiciousPattern: hasSuspiciousPattern
                ? matchedPattern
                : undefined,
            }

            // Add breadcrumb to Sentry with warning level if suspicious
            Sentry.addBreadcrumb({
              type: 'debug',
              category: hasSuspiciousPattern
                ? 'dom.script.suspicious'
                : 'dom.script',
              message: hasSuspiciousPattern
                ? `⚠️ SUSPICIOUS inline script (contains ${matchedPattern})`
                : `Script injected: ${scriptInfo.src}`,
              level: hasSuspiciousPattern ? 'warning' : 'info',
              data: scriptInfo,
            })

            // Also log to console in development for immediate feedback
            if (import.meta.dev) {
              if (hasSuspiciousPattern) {
                // eslint-disable-next-line no-console
                console.warn('⚠️ SUSPICIOUS script injected:', scriptInfo)
              } else {
                // eslint-disable-next-line no-console
                console.debug('📜 Script injected:', scriptInfo)
              }
            }
          }
        }
      }
    })

    // Start observing the document for script injections
    observer.observe(document.documentElement, {
      childList: true, // Watch for added/removed children
      subtree: true, // Watch entire tree, not just direct children
    })

    // Cleanup on unmount (though in practice the observer will be GC'd)
    if (typeof window !== 'undefined') {
      window.addEventListener('unload', () => {
        observer.disconnect()
      })
    }
  }
})
